import {
  type FilesAzureSettingsSchema,
  type FilesB2SettingsSchema,
  type FilesDropboxSettingsSchema,
  type FilesFileSettingsSchema,
  type FilesFtpSettingsSchema,
  type FilesGoogleCloudStorageSettingsSchema,
  type FilesHttpGetStoragesByStorageIdFilesQuery,
  type FilesHttpGetStoragesQuery,
  type FilesHttpSettingsSchema,
  type FilesPortalSettingsSchema,
  type FilesS3SettingsSchema,
  type FilesSftpSettingsSchema,
  type FilesStorageReadSchema,
  type FilesStorageSchema,
  type FilesStorageSchemaPartial,
  type FilesStorageSchemaWithIdNullable,
  httpGetStorages,
  httpGetStoragesByStorageIdFiles,
  httpPatchStoragesByStorageId,
  httpPostStorages,
} from '@iconik-io/iconik-api/src/services/files';

// TODO BE-4812: drop this file once the storage queries declare their list
// filters and the settings are discriminated by method.

type FilesStorageSchemaOmit = Omit<
  FilesStorageReadSchema,
  'id' | 'settings' | 'method'
>;
export type FilesStorageFileSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesFileSettingsSchema;
  method: 'FILE';
};
export type FilesStorageAzureSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesAzureSettingsSchema;
  method: 'AZURE';
};
export type FilesStorageGcsSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesGoogleCloudStorageSettingsSchema;
  method: 'GCS';
};
export type FilesStorageB2SchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesB2SettingsSchema;
  method: 'B2';
};
export type FilesStorageS3SchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesS3SettingsSchema;
  method: 'S3';
};
export type FilesStorageHttpSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesHttpSettingsSchema & {
    write?: boolean;
  };
  method: 'HTTP';
};
export type FilesStorageFtpSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesFtpSettingsSchema;
  method: 'FTP';
};
export type FilesStorageSftpSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesSftpSettingsSchema;
  method: 'SFTP';
};
export type FilesStoragePortalSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesPortalSettingsSchema;
  method: 'PORTAL';
};
export type FilesStorageDropboxSchemaPatched = FilesStorageSchemaOmit & {
  id: string;
  settings: FilesDropboxSettingsSchema;
  method: 'DROPBOX';
};
export type FilesStorageSchemaPatched =
  | FilesStorageFileSchemaPatched
  | FilesStorageAzureSchemaPatched
  | FilesStorageGcsSchemaPatched
  | FilesStorageB2SchemaPatched
  | FilesStorageS3SchemaPatched
  | FilesStorageHttpSchemaPatched
  | FilesStorageFtpSchemaPatched
  | FilesStorageSftpSchemaPatched
  | FilesStoragePortalSchemaPatched
  | FilesStorageDropboxSchemaPatched;

/**
 * `purpose` and `method` are generated as single strings, but the client
 * serializes arrays with `arrayFormat: 'repeat'` and the endpoint filters on
 * every value given.
 */
export type FilesHttpGetStoragesQueryPatched = Omit<
  FilesHttpGetStoragesQuery,
  'purpose' | 'method'
> & {
  purpose?: string | string[];
  method?: string | string[];
};

export const getStoragesRequest = (query: FilesHttpGetStoragesQueryPatched) =>
  // biome-ignore lint/plugin: the generated endpoint still declares the single-string purpose and method, see the type above.
  httpGetStorages(query as FilesHttpGetStoragesQuery);

/** `status` is a list here too; see FilesHttpGetStoragesQueryPatched above. */
export type FilesHttpGetStoragesByStorageIdFilesQueryPatched = Omit<
  FilesHttpGetStoragesByStorageIdFilesQuery,
  'status'
> & {
  status?: string | string[];
};

export const getStorageFilesRequest = (
  storageId: string,
  query: FilesHttpGetStoragesByStorageIdFilesQueryPatched,
) =>
  httpGetStoragesByStorageIdFiles(
    storageId,
    // biome-ignore lint/plugin: the generated endpoint still declares the single-string status, see the type above.
    query as FilesHttpGetStoragesByStorageIdFilesQuery,
  );

/**
 * The generated schema types the settings as one shared bag, while the patched
 * shapes above pair each `method` with the settings it actually carries. The
 * widenings back to the generated request bodies are stated once here.
 */
/** The response carries the same paired settings, back the other way. */
export const asPatchedStorage = (
  body: FilesStorageReadSchema | FilesStorageSchemaWithIdNullable,
): FilesStorageSchemaPatched =>
  // biome-ignore lint/plugin: see createStorageRequest.
  body as FilesStorageSchemaPatched;

export const createStorageRequest = (storage: FilesStorageSchemaPatched) =>
  // biome-ignore lint/plugin: see above.
  httpPostStorages(storage as FilesStorageSchema);

export const updateStorageRequest = (
  storageId: string,
  storage: Partial<FilesStorageSchemaPatched>,
) =>
  httpPatchStoragesByStorageId(
    storageId,
    // biome-ignore lint/plugin: see createStorageRequest.
    storage as FilesStorageSchemaPartial,
  );
